import { fetchWithAuth } from 'utils/fetchWithAuth';
import { unwrapResponse as unwrap } from 'utils/unwrapResponse';

export type SectorDesk = 3 | 205;
export const DESK_NAME: Record<SectorDesk, string> = { 3: 'LUXE', 205: 'DPGP' };
export const DESKS = Object.keys(DESK_NAME).map(Number) as SectorDesk[];
export const deskNameOf = (sd: unknown): string | undefined => DESK_NAME[Number(sd) as SectorDesk];

/** AC18: the journal filtered on one realignment's parent and children. */
export const journalLink = (realignmentId: string): string =>
  `/admin/audit?module=referentials&realignmentId=${encodeURIComponent(realignmentId)}`;

export interface AlignmentTicket {
  key: string;
  summary: string | null;
  status: string | null;
  closed: boolean;
  sector: string | null;
}
export interface StoreDrift {
  store: string;
  currentSector: string;
  fromSectors: { sector: string | null; count: number }[];
  tickets: AlignmentTicket[];
}
export interface JiraFix {
  reason: 'no-store' | 'no-sector';
  store: string | null;
  tickets: AlignmentTicket[];
}
export interface EcmUser {
  id: number;
  name: string;
  email: string | null;
  sectors: string[];
}
export type EcmIssue =
  | { kind: 'sector-without-ecm'; sector: string; tickets: number }
  | { kind: 'unknown-sector'; user: EcmUser; sector: string; suggestion: string | null }
  | { kind: 'no-sector'; user: EcmUser }
  | { kind: 'multiple-sectors'; user: EcmUser }
  | { kind: 'shared-sector'; sector: string; users: EcmUser[] };
export interface Transition {
  from: string | null;
  to: string;
  tickets: number;
  losing: EcmUser[];
  gaining: EcmUser[];
}
export interface DeskAnalysis {
  sd: SectorDesk;
  status: 'ok' | 'error';
  error?: string;
  misaligned: number;
  closed: number;
  stores: StoreDrift[];
  unreadable: number;
  jira: JiraFix[];
  ecm: EcmIssue[];
  impact: Transition[];
  analyzedAt: string;
}
export type LastRun = {
  id: string;
  realignmentId: string;
  at: string;
  actor: { id: number; name: string } | null;
  sd: SectorDesk;
  total: number;
  applied: number;
  failed: number;
  outcome: 'done' | 'partial' | 'interrupted';
  failures: { key: string; reason: string | null }[];
} | null;
export interface SectorAnalysis {
  desks: DeskAnalysis[];
  lastRun: LastRun;
}

export type JobState = 'waiting' | 'active' | 'delayed' | 'completed' | 'failed' | 'idle';
export interface RealignmentJob {
  state: JobState;
  sd?: SectorDesk;
  progress?: { done: number; total: number };
  startedBy?: string | null;
  startedAt?: string;
}

/** A run is already in flight (409): carries who started it and when, for the page's toast. */
export class RealignmentConflictError extends Error {
  readonly startedBy: string | null;
  readonly startedAt: string | null;

  constructor(startedBy: string | null, startedAt: string | null) {
    super('A sector realignment is already running');
    this.name = 'RealignmentConflictError';
    this.startedBy = startedBy;
    this.startedAt = startedAt;
  }
}

const BASE = 'admin/sector-alignment';

export const sectorAlignmentApi = {
  analysis: async (sd?: SectorDesk): Promise<SectorAnalysis> =>
    unwrap<SectorAnalysis>(await fetchWithAuth(`${BASE}/analysis${sd ? `?sd=${sd}` : ''}`), 'Failed to analyze the sector alignment'),

  realign: async (sd: SectorDesk): Promise<{ jobId: string }> => {
    const res = await fetchWithAuth(`${BASE}/realign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sd }),
    });
    if (res.status === 409) {
      const body = await res.json().catch(() => null);
      throw new RealignmentConflictError(body?.details?.startedBy ?? null, body?.details?.startedAt ?? null);
    }
    return unwrap<{ jobId: string }>(res, 'Failed to start the realignment');
  },

  job: async (): Promise<RealignmentJob> =>
    unwrap<RealignmentJob>(await fetchWithAuth(`${BASE}/job`), 'Failed to fetch the realignment job'),
};
